import Button from '@shared/components/Button/Button';
import Divider from '@shared/components/Divider/Divider';
import Popup from '@shared/components/Popup/Popup';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as CheckCircle } from 'assets/icons/Check.svg';
import CloseButton from 'components/CloseButton';
import GradientIconWithShadow from 'components/GradientIconWithShadow/GradientIconWithShadow';

import { useCreateSecurityCodeDataContext } from './CreateSecurityCodeContext';

const SuccessfullyCreatedPasscodeModal = () => {
  const { t } = useTranslation();
  const { close, onSuccess, onLogout, mode } = useCreateSecurityCodeDataContext();
  const isChangeMode = mode === 'change';
  const isLogoutFlow = !!onLogout;

  const handleDone = useCallback(() => {
    onSuccess?.();
    close();
  }, [close, onSuccess]);

  const handleLogout = useCallback(() => {
    onSuccess?.();
    onLogout?.();
    close();
  }, [close, onSuccess, onLogout]);

  const handleStayLoggedIn = useCallback(() => {
    onSuccess?.();
    close();
  }, [close, onSuccess]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === 'Escape') {
        e.preventDefault();
        handleDone();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleDone]);

  return (
    <Popup isVisible closePopup={handleDone}>
      <Popup.FullScreenContainer className="relative border border-gray-30 !bg-gray-10 md:w-[512px] md:min-w-[512px] md:rounded-2xl">
        <CloseButton onClick={handleDone} className="absolute right-[15px] top-[15px] z-10 !bg-gray-10" />
        <Popup.ScrollContainer className="px-6 pt-12 pb-6">
          <div className="flex flex-col items-center gap-8">
            <GradientIconWithShadow icon={CheckCircle} iconClassName="!stroke-1.5 stroke-primary-60" />
            <div className="flex flex-col items-center gap-2">
              <p className="text-center text-xl font-semibold leading-[1.25] text-black">
                {t(
                  isChangeMode ? 'verification.security_code_changed_title' : 'verification.security_code_created_title'
                )}
              </p>
              <p className="text-center font-light leading-[1.5] text-black">
                {isLogoutFlow
                  ? t('verification.security_code_created_logout_description')
                  : isChangeMode
                  ? t('verification.security_code_changed_description')
                  : t('verification.security_code_created_description')}
              </p>
            </div>
          </div>
        </Popup.ScrollContainer>
        {isLogoutFlow ? (
          <div className="flex flex-col gap-4 px-6 pb-6">
            <Divider />
            <div className="flex items-center justify-end gap-4">
              <Button variant="tertiary" size="md" onClick={handleStayLoggedIn}>
                {t('verification.stay_logged_in')}
              </Button>
              <Button variant="secondary" size="md" onClick={handleLogout}>
                {t('verification.log_out')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex justify-center px-6 pb-6">
            <Button variant="primary" size="md" className="h-12 min-w-[90px] rounded-2xl px-6" onClick={handleDone}>
              {t('verification.security_code_done_cta')}
            </Button>
          </div>
        )}
      </Popup.FullScreenContainer>
    </Popup>
  );
};

export default SuccessfullyCreatedPasscodeModal;
